import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TrainDeviation } from '../../../models/train-deviation.model';
import { DriverInfo } from '../../../models/driver-info.model';
import { DriverDeviationSelection } from '../../../models/resolve-deviation-request.model';

export interface DriverRouteGroup {
  groupId: string;
  routeLabel: string;
  timeLabel: string;
  drivers: DriverInfo[];
  selectedDriverId: string | null;
}

export interface ResolveConflictDialogResult {
  action: 'save';
  selections: DriverDeviationSelection[];
  comment: string;
}

@Component({
  selector: 'app-resolve-conflict-dialog',
  standalone: false,
  templateUrl: './resolve-conflict-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./resolve-conflict-dialog.component.scss']
})
export class ResolveConflictDialogComponent {
  driverGroups: DriverRouteGroup[] = [];
  comment: string = '';

  constructor(
    public dialogRef: MatDialogRef<ResolveConflictDialogComponent, ResolveConflictDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: TrainDeviation
  ) {
    this.comment = data.note || '';
    this.driverGroups = this.buildDriverGroups();
  }

  get isMultiGroup(): boolean {
    return this.driverGroups.length > 1;
  }

  private buildDriverGroups(): DriverRouteGroup[] {
    return this.data.conflictGroups.map(group => {
      const { origin, destination, departureAtUtc, arrivalAtUtc } = group.route;
      const routeLabel = `${origin} → ${destination}`;
      const timeLabel = `${this.formatTime(departureAtUtc)} – ${this.formatTime(arrivalAtUtc)}`;

      // Restore any already-selected driver (e.g. re-opening a handled deviation).
      const selected = group.trainDrivers.find(d => d.isSelected);
      const selectedDriverId = selected?.id ?? null;

      return { groupId: group.id, routeLabel, timeLabel, drivers: group.trainDrivers, selectedDriverId };
    });
  }

  selectDriver(group: DriverRouteGroup, driver: DriverInfo): void {
    if (driver.id) {
      group.selectedDriverId = driver.id;
    }
  }

  isDriverSelected(group: DriverRouteGroup, driver: DriverInfo): boolean {
    return driver.id === group.selectedDriverId;
  }

  canSave(): boolean {
    return this.driverGroups.length > 0 &&
      this.driverGroups.every(g => g.selectedDriverId !== null);
  }

  formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  }

  getRoleLabel(role: string): string {
    switch (role) {
      case 'PLANNED':     return 'Planerad';
      case 'DEVIATING':   return 'Avvikande';
      case 'NOT_SELECTED': return 'Ej vald';
      default:            return role;
    }
  }

  getRoleClass(role: string): string {
    switch (role) {
      case 'PLANNED':     return 'role-planned';
      case 'DEVIATING':   return 'role-deviating';
      case 'NOT_SELECTED': return 'role-not-selected';
      default:            return '';
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.canSave()) return;

    const selections = this.driverGroups
      .filter((g): g is DriverRouteGroup & { selectedDriverId: string } => g.selectedDriverId !== null)
      .map(g => new DriverDeviationSelection(g.groupId, g.selectedDriverId));

    this.dialogRef.close({
      action: 'save',
      selections,
      comment: this.comment
    });
  }
}
